/**
 * PlayGameUnity Component
 * Unity WebGL game iframe integration with lifecycle management
 * Handles game loading, fullscreen, and navigation
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { ArrowsPointingInIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import invariant from 'invariant'
import { INTERAX_COLORS } from '../../../constants/colors'
import { Button as DesignSystemButton } from '../../../interaxDesignSystemComponents'
import Spinner from '../../../interaxDesignSystemComponents/Spinner'
import { GameParams, GAME_NAME, getGameUrl } from '../../../libs/GameHelper'
import {
  computeLatchedGameUrl,
  hasPlayedEveryConfiguredLoop,
  hasTeamMovedOnToReflection,
  resolveEffectiveGameUrl,
  shouldBlockOnSessionSpinner,
  shouldReleaseCallForExhaustedGame
} from '../helpers/gameSessionLatch'
import { useSessionId } from '../../../hooks/useSessionId'
import { useSessionIdFacilitator } from '../../../hooks/useSessionIdFacilitator'
import { useTeam } from '../../../apiLayer'
import { useUser } from '../../../hooks/useUser'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useLifeCycleMethods, getUnityInstance } from '../helpers/useLifeCycleMethods'
import { useLeaveGameWhenForceClosed } from '../helpers/useLeaveGameWhenForceClosed'
import { useAIDebriefRouteAfterGame } from '../hooks/useAIDebriefRouteAfterGame'
import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '../../../apiLayer/queryKeys'
import { UNITY_SHELL_BACKGROUND, useNormalizeUnityFrameStyles } from '../hooks/useNormalizeUnityFrameStyles'
import { useGlobalWhereby } from '../../../shared/hooks/useGlobalWhereby'
import { broadcastLeaveGameCall } from '../../../shared/hooks/wherebyCrossTab'
import { GamerRoutes, FacilitatorRoutes } from '../../../config/routes/routeEnums'
import { useFeatureFlags } from '../../../shared/hooks/useFeatureFlags'
import { ActivityType } from '../../../../../pure-js/enums/ActivityType'
import { WherebyCallKind } from '../../../../../pure-js/enums/Whereby'
import config from '../../../Config'
import { isAgentModeEnabled } from '../../../agentTesting/agentMode'
import { TESTIDS } from '../../../config/testids'
import { useAppDispatch, useAppSelector } from '../../../store'
import { selectGameHudReminder } from '../../../store/selectors/activitiesSelectors'
import { clearGameHudReminder } from '../../../store/slices/activitiesSlice'

// Extend window type for Unity callbacks
declare global {
  interface Window {
    onGameLoaded?: () => void
    onGameEnded?: () => void
    onGameAborted?: () => void
    onTutorialAborted?: () => void
    onTutorialEnded?: () => void
    onLobbyLeft?: () => void
  }
}

// The game object the web talks to. Call presence goes in as a JSON array of { userId, displayName };
// a next-activity reminder as one line of text for the heads-up display.
const GAME_RECEIVER_GAMEOBJECT = 'CommonData'
const WHEREBY_RECEIVER_METHOD = 'OnGroupCallUsersChanged'
const REMINDER_RECEIVER_METHOD = 'OnNextActivityReminder'
// The game's heads-up line is wired end to end but held back: nothing interrupts play until it is wanted.
const IS_NEXT_ACTIVITY_REMINDER_SHOWN_IN_GAME = false

// Activity types that pin the floating call to the game room.
const GAME_PIN_TYPES = new Set<string>([ActivityType.FIRST_GAME, ActivityType.SHORT_GAME, ActivityType.LONG_GAME])

interface PlayGameUnityProps {
  gameParams?: Partial<GameParams>
  className?: string
}

/**
 * PlayGameUnity - Unity game iframe with fullscreen and lifecycle management
 *
 * @param {PlayGameUnityProps} props - Component props.
 * @return {JSX.Element} Game iframe view.
 */
export const PlayGameUnity: React.FC<PlayGameUnityProps> = ({ gameParams, className = '' }) => {
  const gameFrame = useRef<HTMLIFrameElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const { data: user } = useUser()
  const { t } = useTranslation()

  // Detect context for navigation
  const isFacilitatorContext = location.pathname.startsWith('/facilitator')
  const isTutorialMode = gameParams?.introduction === true
  const { isLatestSessionFeedbackEnabled } = useFeatureFlags()

  // Develop mode (config.enableDevelop) relaxes the session/team requirements so the game
  // can be launched for development without an upcoming activity.
  const isDevelopMode = config.enableDevelop

  // Use facilitator-specific hook for facilitators, gamer hook for gamers
  const shouldSkipSessionFetch = isTutorialMode
  const gamerSession = useSessionId({ skip: shouldSkipSessionFetch || isFacilitatorContext })
  const facilitatorSession = useSessionIdFacilitator()

  const {
    sessionId,
    teamId,
    sprintId,
    numberOfLoops,
    customSlotLength,
    minTimeLeftToAllowRestart,
    finalReflectionTimeOut,
    isLoading: isLoadingSession
  } = isFacilitatorContext
    ? {
        sessionId: facilitatorSession.data,
        teamId: facilitatorSession.teamId,
        sprintId: facilitatorSession.sprintId,
        numberOfLoops: facilitatorSession.numberOfLoops,
        customSlotLength: facilitatorSession.customSlotLength,
        minTimeLeftToAllowRestart: facilitatorSession.minTimeLeftToAllowRestart,
        finalReflectionTimeOut: facilitatorSession.finalReflectionTimeOut,
        isLoading: facilitatorSession.isLoading
      }
    : gamerSession

  // Full game-session latch: freeze the resolved session/team/sprint the moment the game launches, so the
  // staleTime:0 resolver re-deriving mid-play can't change what the live game runs on (restores May behaviour).
  const latchedSessionRef = useRef<{
    sessionId?: string
    teamId?: string
    sprintId?: string
    activityId?: string
  } | null>(null)
  if (!latchedSessionRef.current && !isTutorialMode && !isFacilitatorContext && sessionId && teamId) {
    latchedSessionRef.current = { sessionId, teamId, sprintId, activityId: gamerSession.activityId }
  }
  const effectiveSessionId = latchedSessionRef.current?.sessionId ?? sessionId
  const effectiveTeamId = latchedSessionRef.current?.teamId ?? teamId
  const effectiveSprintId = latchedSessionRef.current?.sprintId ?? sprintId

  const [showFullScreenButton, setShowFullScreenButton] = useState(false)
  // Latched once the game has launched with a valid session — see the latch effect below. A live game must
  // never be re-derived from the (staleTime:0) upcoming-activity query mid-play.
  const [latchedGameUrl, setLatchedGameUrl] = useState<string | null>(null)
  const {
    killCall,
    startCall,
    isCallActive,
    setReconnectPending,
    participants: wherebyParticipants,
    setCallScope
  } = useGlobalWhereby()
  // only try to start the call once, so we never drag someone back in after the call ended
  const selfStartAttemptedRef = useRef(false)

  // Pin the call to this game so the floating call stays on the game room across refresh/schedule drift.
  // Only for a real gamer game session; never overwrite with a non-game (e.g. the AI coach after overrun).
  useEffect(() => {
    if (isFacilitatorContext || isTutorialMode) return
    const {
      sessionId: gameSessionId,
      sprintId: gameSprintId,
      activityId: gameActivityId,
      teamId: gameTeamId,
      activityType
    } = gamerSession
    if (gameSprintId && gameActivityId && gameTeamId && activityType && GAME_PIN_TYPES.has(activityType)) {
      setCallScope({
        kind: WherebyCallKind.GAME,
        sprintId: gameSprintId,
        activityId: gameActivityId,
        teamId: gameTeamId,
        activityType,
        sessionId: gameSessionId
      })
      // if someone landed on the game page directly, the call was never started — start it for them
      if (!selfStartAttemptedRef.current && numberOfLoops !== 0) {
        selfStartAttemptedRef.current = true
        if (!isCallActive) startCall()
      }
    }
  }, [gamerSession, isFacilitatorContext, isTutorialMode, numberOfLoops, setCallScope, isCallActive, startCall])

  const onGameComplete = useCallback(() => {
    killCall()
    // Tell sibling tabs of this browser to drop any stale game call for this team, so a second tab left
    // open on the game can't keep the room alive and block the AI coach transcript/analysis.
    const latched = latchedSessionRef.current
    broadcastLeaveGameCall({ teamId: latched?.teamId ?? teamId, sprintId: latched?.sprintId ?? sprintId })
    // The reconnect resolves its room from the current activity; a cached copy still says this game.
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER] })
  }, [killCall, queryClient, teamId, sprintId])

  const onRejoinCall = useCallback(() => setReconnectPending(true), [setReconnectPending])

  // Get team data — the query stays disabled until we actually have a teamId.
  const { data: teamResponse, isLoading: isLoadingTeam } = useTeam(effectiveTeamId || '', {
    enabled: !!effectiveTeamId
  })
  const team = teamResponse?.data

  const resolveDebriefRoute = useAIDebriefRouteAfterGame({
    sprintId: latchedSessionRef.current?.sprintId,
    activityId: latchedSessionRef.current?.activityId,
    teamId: latchedSessionRef.current?.teamId
  })

  // Setup Unity lifecycle callbacks
  const { onIframeLoad, isGameLoaded, leaveGame } = useLifeCycleMethods(
    gameFrame,
    effectiveSessionId || '',
    navigate,
    setShowFullScreenButton,
    isFacilitatorContext,
    isLatestSessionFeedbackEnabled,
    { onGameComplete, onRejoinCall, resolveDebriefRoute }
  )
  // An operator's Back Office close ends the game for everyone in it; the latch ignores every other session change.
  useLeaveGameWhenForceClosed(latchedSessionRef.current?.teamId, latchedSessionRef.current?.sessionId, leaveGame)
  const normalizeUnityFrameStyles = useNormalizeUnityFrameStyles(gameFrame)

  // Push the connected Whereby participants into the running game — on load and on every change.
  const wherebyParticipantsRef = useRef(wherebyParticipants)
  wherebyParticipantsRef.current = wherebyParticipants
  useEffect(() => {
    if (!isGameLoaded) return
    const instance = getUnityInstance(gameFrame)
    if (typeof instance?.SendMessage !== 'function') {
      console.warn('[Whereby→Unity] OnGroupCallUsersChanged skipped: Unity instance/SendMessage not ready')
      return
    }
    try {
      // JSON array of { userId, displayName } — userId is the Whereby externalId (our app user id).
      const payload = JSON.stringify(wherebyParticipantsRef.current)
      instance.SendMessage(GAME_RECEIVER_GAMEOBJECT, WHEREBY_RECEIVER_METHOD, payload)
    } catch (e) {
      // Game not ready / GameObject not in scene yet — ignore; the next change re-sends.
      console.warn('[Whereby→Unity] OnGroupCallUsersChanged SendMessage threw', e)
    }
  }, [wherebyParticipants, isGameLoaded])

  // A next-activity reminder raised while playing: the page is behind the game's canvas, so the game shows it.
  const dispatch = useAppDispatch()
  const gameHudReminder = useAppSelector(selectGameHudReminder)
  useEffect(() => {
    if (!gameHudReminder || !isGameLoaded) return
    const instance = getUnityInstance(gameFrame)
    if (typeof instance?.SendMessage !== 'function') return
    if (IS_NEXT_ACTIVITY_REMINDER_SHOWN_IN_GAME) {
      try {
        instance.SendMessage(GAME_RECEIVER_GAMEOBJECT, REMINDER_RECEIVER_METHOD, gameHudReminder.message)
      } catch (error) {
        console.warn('[Reminder→Unity] OnNextActivityReminder SendMessage threw', error)
      }
    }
    dispatch(clearGameHudReminder())
  }, [gameHudReminder, isGameLoaded, dispatch])
  // Leaving the page drops a reminder the game never got to show — it must not surface on a later game.
  useEffect(
    () => () => {
      dispatch(clearGameHudReminder())
    },
    [dispatch]
  )

  // AI-agentic-testing: expose window.__agentGameApi once the game is loaded (dev + agent mode only).
  useEffect(() => {
    if (!isGameLoaded || !isAgentModeEnabled()) return
    let cleanup: (() => void) | undefined
    let disposed = false
    void import('../../../agentTesting/agentGameApi').then((m) => {
      if (disposed) return
      cleanup = m.installAgentGameApi(gameFrame)
    })
    return () => {
      disposed = true
      cleanup?.()
    }
  }, [isGameLoaded])

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Player'
  const teamDisplayName = team?.name || 'Team'

  const goToFullScreen = () => {
    const instance = getUnityInstance(gameFrame)
    invariant(instance, 'Unity instance not found')
    instance.SetFullscreen(1)
  }

  const handleBackClick = () => {
    if (isTutorialMode) {
      navigate(GamerRoutes.ONBOARDING)
      return
    }

    const dashboardRoute = isFacilitatorContext ? FacilitatorRoutes.DASHBOARD : GamerRoutes.DASHBOARD
    navigate(dashboardRoute)
  }

  const handleIframeLoad = () => {
    normalizeUnityFrameStyles()
    onIframeLoad()
  }

  // Game-context readiness (non-tutorial mode only).
  // Production needs a real session + team before the game can start; develop mode may
  // launch without them so the game can be tested without an upcoming activity.
  const hasSession = !!effectiveSessionId
  const hasTeam = !!effectiveTeamId
  const requiresFullGameContext = !isDevelopMode
  const hasPlayedEveryLoop = hasPlayedEveryConfiguredLoop({ numberOfLoops, isLoopedGame: !isTutorialMode })
  const isGameOverForTeam =
    hasPlayedEveryLoop ||
    hasTeamMovedOnToReflection({
      activityType: gamerSession.activityType,
      isPlayerGamePage: !isTutorialMode && !isFacilitatorContext
    })
  const hasReleasedExhaustedCallRef = useRef(false)
  useEffect(() => {
    if (hasReleasedExhaustedCallRef.current || !shouldReleaseCallForExhaustedGame(hasPlayedEveryLoop, latchedGameUrl))
      return

    hasReleasedExhaustedCallRef.current = true
    killCall()
    broadcastLeaveGameCall({ teamId, sprintId })
    setCallScope(null)
  }, [hasPlayedEveryLoop, killCall, latchedGameUrl, setCallScope, sprintId, teamId])
  const isMissingGameContext = !hasSession || !hasTeam

  const isFetchingTeam = !shouldSkipSessionFetch && hasTeam
  const isLoadingSessionContext = shouldSkipSessionFetch ? false : isLoadingSession
  const isLoadingTeamContext = isFetchingTeam && isLoadingTeam
  const isLoadingGameContext = isLoadingSessionContext || isLoadingTeamContext

  // A game that is over blocks in EVERY environment, unlike the session/team requirement that develop
  // deliberately relaxes: a run with no loop count does not stop, which is not a thing to test around.
  const isBlockedByMissingContext = (requiresFullGameContext && isMissingGameContext) || isGameOverForTeam
  const shouldWaitForSession = !isTutorialMode && (isLoadingGameContext || isBlockedByMissingContext)

  // Generate the game URL with the full session/team context.
  // Tutorial mode — and develop mode without real data — launch the game without that context.
  const launchesWithoutSession = isTutorialMode || (isDevelopMode && !hasSession)
  const launchesWithoutTeamContext = isTutorialMode || (isDevelopMode && !hasTeam)
  const resolvedSessionId = launchesWithoutSession ? '' : effectiveSessionId || ''
  const gameTeamContext = launchesWithoutTeamContext
    ? {}
    : {
        teamId: effectiveTeamId || '',
        sprintId: effectiveSprintId || '',
        numberOfLoops,
        customSlotLength,
        minTimeLeftToAllowRestart,
        finalReflectionTimeOut
      }

  const gameUrl = getGameUrl({
    displayName,
    ...gameParams,
    sessionId: resolvedSessionId,
    teamDisplayName,
    ...gameTeamContext
  })

  // Latch the URL the first time the game is ready to launch. Once latched, the running game is pinned to
  // THIS session and never re-derived from the upcoming-activity query again. Without this, when the backend
  // stops resolving the game mid-play (e.g. a premature /Session/Close flips the game session to 'completed',
  // so the resolver returns the reflection instead), useSessionId returns a new/empty session, the iframe src
  // changes (or the spinner branch unmounts the iframe), and the player is torn out of the Photon room — all
  // players at once = the whole team kicked mid-loop. A live game must only end via Unity's onGameEnded.
  useEffect(() => {
    const next = computeLatchedGameUrl(latchedGameUrl, shouldWaitForSession, gameUrl)
    if (next !== latchedGameUrl) {
      setLatchedGameUrl(next)
    }
  }, [latchedGameUrl, shouldWaitForSession, gameUrl])

  const effectiveGameUrl = resolveEffectiveGameUrl(latchedGameUrl, gameUrl)

  const isUpcomingActivityMissing = !isGameOverForTeam && !isLoadingSession && !hasTeam

  // Only show the loading spinner BEFORE the game has launched. Once latched, keep the live game mounted
  // regardless of any later change in the resolved session.
  if (shouldBlockOnSessionSpinner(latchedGameUrl, shouldWaitForSession)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        {/* Nothing is loading once the game is over — a spinner there would just never finish. */}
        {!isGameOverForTeam && <Spinner size="large" color="sun" text={t('gamer.gameCenter.loadingGameSession')} />}
        {isGameOverForTeam && (
          <Typography sx={{ color: INTERAX_COLORS.white, fontSize: '14px', opacity: 0.7 }}>
            {t('gamer.gameCenter.allLoopsPlayed')}
          </Typography>
        )}
        {isUpcomingActivityMissing && (
          <Typography sx={{ color: INTERAX_COLORS.white, fontSize: '14px', opacity: 0.7 }}>
            {t('gamer.gameCenter.noUpcomingActivities')}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box className={className} sx={{ width: '100%' }}>
      {/* Header with back button and fullscreen button */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        {/* Back button - icon only */}
        <DesignSystemButton
          variant="secondary"
          onClick={handleBackClick}
          icon={<ChevronLeftIcon className="w-6 h-6" />}
          iconPosition="left"
          data-testid={TESTIDS.PLAY_GAME_BACK_BTN}
        />

        {/* Fullscreen button */}
        <Box sx={{ visibility: showFullScreenButton ? 'visible' : 'hidden' }}>
          <DesignSystemButton
            variant="primary"
            onClick={goToFullScreen}
            icon={<ArrowsPointingInIcon className="w-5 h-5" />}
            iconPosition="right"
            data-testid={TESTIDS.PLAY_GAME_FULLSCREEN_BTN}
          >
            {t('gamer.gameCenter.makeFullScreen')}
          </DesignSystemButton>
        </Box>

        {/* Spacer to balance layout */}
        <Box sx={{ width: '40px' }} />
      </Box>

      {/* Game iframe - full width, global floating call handles Whereby */}
      <Box sx={{ width: '100%', px: 2 }}>
        <iframe
          key="game-frame"
          ref={gameFrame}
          title={GAME_NAME}
          src={effectiveGameUrl}
          width="100%"
          height="650px"
          style={{
            border: 'none',
            textAlign: 'center',
            alignItems: 'center',
            display: 'flex',
            backgroundColor: UNITY_SHELL_BACKGROUND
          }}
          onLoad={handleIframeLoad}
          onError={() => {}}
        />
      </Box>
    </Box>
  )
}
